/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { MapData, MapEdgeType, MapNodeType, MapUpdateCheckData } from "client";
import {
  useContext,
  createContext,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
  FC,
  useState,
  useCallback,
} from "react";

interface MapDataContext {
  mapData: MapData;
  mapVer: MapUpdateCheckData;
  selectedRouteElement?: MapNodeType | MapEdgeType;
  setMapData: Dispatch<SetStateAction<MapData>>;
  setMapVer: Dispatch<SetStateAction<MapUpdateCheckData>>;
  setSelectedRouteElement: Dispatch<
    SetStateAction<MapNodeType | MapEdgeType | undefined>
  >;
  reset: () => void;
}

const defaultValue: MapDataContext = {
  mapData: {
    mapId: "",
    mapVer: 0,
    mapName: "",
    originLat: 0,
    originLon: 0,
    originAlt: 0,
    mapInfo: [],
  },
  mapVer: {
    mapVer: 0,
    necessityOfUpdate: false,
  },
  setMapData: (value) => {
    // default value's function is not called.
  },
  setMapVer: (value) => {
    // default value's function is not called.
  },
  setSelectedRouteElement: (value) => {
    // default value's function is not called.
  },
  reset: () => {
    // default value's function is not called.
  },
};

const context = createContext<MapDataContext>(defaultValue);

export const MapDataProvider: FC<PropsWithChildren> = ({ children }) => {
  const [mapData, setMapData] = useState<MapData>(defaultValue.mapData);
  const [mapVer, setMapVer] = useState<MapUpdateCheckData>(defaultValue.mapVer);
  const [selectedRouteElement, setSelectedRouteElement] = useState<
    MapNodeType | MapEdgeType
  >();
  const reset = useCallback(() => {
    setMapData(defaultValue.mapData);
    setMapVer(defaultValue.mapVer);
    setSelectedRouteElement(undefined);
  }, []);

  return (
    <context.Provider
      value={{
        mapData,
        mapVer,
        selectedRouteElement,
        setMapData,
        setMapVer,
        setSelectedRouteElement,
        reset,
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useMapDataContext = () => {
  return useContext(context);
};
